<script setup lang="ts">
import { computed } from "vue";
import { useQuery } from "@tanstack/vue-query";

import { getDownloadUrl } from "@/api/client";

const props = withDefaults(
  defineProps<{
    name: string;
    fileId?: string | null;
    size?: number;
  }>(),
  { fileId: null, size: 36 },
);

// Presigned URLs are short-lived + per-file; cache by file id so the same
// worker's avatar isn't re-fetched across the list / re-renders.
const { data: url } = useQuery({
  queryKey: ["file-url", computed(() => props.fileId)],
  queryFn: () => getDownloadUrl(props.fileId as string),
  enabled: () => !!props.fileId,
  staleTime: 10 * 60 * 1000,
});

const initials = computed(() =>
  props.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join(""),
);
</script>

<template>
  <span
    class="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-100 text-xs font-semibold text-neutral-500"
    :style="{ width: `${size}px`, height: `${size}px` }"
  >
    <img
      v-if="url"
      :src="url"
      :alt="name"
      class="h-full w-full object-cover"
    >
    <span v-else>{{ initials || "—" }}</span>
  </span>
</template>
